import { BarChartSeries } from "@/components/reporting/BarChart.tsx";
import { DataBarsRow } from "@/components/data-bars/DataBars.tsx";
import {
  ApiWidgetColumn,
  ApiWidgetColumnKey,
  ApiWidgetData,
} from "@/types/reporting/reporting.types.ts";
import { colorCssVariables, type ColorName } from "@skailer-com/uikit";

const CROSS_TAB_COLORS: readonly ColorName[] = [
  "new",
  "blue",
  "violet",
  "pink",
  "red",
  "yellow",
  "green",
  "cyan",
];

type PeriodDatesFormatted = { title: string } | null;
type FormatPeriodDates = (args: { dateStart?: Date; dateEnd?: Date }) => PeriodDatesFormatted;

export interface LineDataFromWidget {
  labels: string[];
  data: number[];
}

export interface BarDataFromWidget {
  labels: string[];
  series: BarChartSeries[];
}

export interface PieDataFromWidget {
  labels: string[];
  data: number[];
}

// API serializes Decimal as a JSON string, so values are coerced with Number().
export const widgetDimensionToBarData = (
  data: ApiWidgetData | undefined,
  label: string,
  color: string = "--color-brand"
): BarDataFromWidget => {
  const points = data?.series[0]?.points ?? [];
  return {
    labels: points.map((point) => point.label),
    series: [{ label, data: points.map((point) => Number(point.value ?? 0)), color }],
  };
};

// Series are aligned by index: the backend zero-fills missing combinations.
export const widgetCrossTabToBarData = (data: ApiWidgetData | undefined): BarDataFromWidget => {
  const series = data?.series ?? [];
  return {
    labels: series[0]?.points.map((point) => point.label) ?? [],
    series: series.map((entry, index) => ({
      label: entry.key,
      data: entry.points.map((point) => Number(point.value ?? 0)),
      color: colorCssVariables[CROSS_TAB_COLORS[index % CROSS_TAB_COLORS.length]],
    })),
  };
};

export const widgetDimensionToPieData = (data: ApiWidgetData | undefined): PieDataFromWidget => {
  const points = data?.series[0]?.points ?? [];
  return {
    labels: points.map((point) => point.label),
    data: points.map((point) => Number(point.value ?? 0)),
  };
};

export interface TableDataFromWidget {
  columns: ApiWidgetColumn[];
  rows: (string | number | null)[][];
}

export const widgetDimensionToTableData = (
  data: ApiWidgetData | undefined
): TableDataFromWidget => {
  const points = data?.series[0]?.points ?? [];
  const baseColumns = data?.columns ?? [];
  const columns: ApiWidgetColumn[] = [...baseColumns, { key: ApiWidgetColumnKey.employees }];
  const rows = points.map((point) => [
    point.label,
    Number(point.value ?? 0),
    point.entity_ids.length,
  ]);
  return { columns, rows };
};

// API serializes Decimal as a JSON string, so values are coerced with Number().
export const widgetSeriesToLineData = (
  data: ApiWidgetData | undefined,
  formatPeriodDates: FormatPeriodDates
): LineDataFromWidget => {
  const points = data?.series[0]?.points ?? [];
  const labels: string[] = [];
  const values: number[] = [];

  for (const point of points) {
    labels.push(
      formatPeriodDates({
        dateStart: point.date_from ?? undefined,
        dateEnd: point.date_to ?? undefined,
      })?.title || ""
    );
    values.push(Number(point.value ?? 0));
  }

  return { labels, data: values };
};

export const widgetPointEntityIds = (
  data: ApiWidgetData | undefined,
  pointIndex: number,
  seriesIndex: number = 0
): number[] => {
  return data?.series[seriesIndex]?.points[pointIndex]?.entity_ids ?? [];
};

export const buildWidgetDataBarsRows = (
  data: ApiWidgetData | undefined,
  formatValue: (value: number) => string,
  onDrill: (employeeIds: number[], label: string) => void
): DataBarsRow[] => {
  const points = data?.series[0]?.points ?? [];
  const maxValue = Math.max(...points.map((point) => Math.abs(Number(point.value ?? 0))), 1);

  return points.map((point) => {
    const value = Number(point.value ?? 0);
    const drillDownIds = point.entity_ids;
    return {
      title: point.label,
      value: formatValue(value),
      sortValue: value,
      percent: (value / maxValue) * 100,
      emoji: null,
      onClick: drillDownIds.length > 0 ? () => onDrill(drillDownIds, point.label) : undefined,
    };
  });
};

const toNullableNumber = (value: number | null): number | null =>
  value === null ? null : Number(value);

export const widgetDimensionToNullableBarData = (
  data: ApiWidgetData | undefined,
  label: string,
  color: string = "--color-brand"
): BarDataFromWidget => {
  const points = data?.series[0]?.points ?? [];
  return {
    labels: points.map((point) => point.label),
    series: [{ label, data: points.map((point) => toNullableNumber(point.value)), color }],
  };
};

export const buildNullableWidgetDataBarsRows = (
  data: ApiWidgetData | undefined,
  formatValue: (value: number) => string,
  onDrill: (employeeIds: number[], label: string) => void
): DataBarsRow[] => {
  const points = data?.series[0]?.points ?? [];
  const maxValue = Math.max(...points.map((point) => Math.abs(Number(point.value ?? 0))), 1);

  return points.map((point) => {
    const value = toNullableNumber(point.value);
    const drillDownIds = point.entity_ids;
    return {
      title: point.label,
      value: value === null ? "-" : formatValue(value),
      sortValue: value ?? -Infinity,
      percent: value === null ? 0 : (value / maxValue) * 100,
      emoji: null,
      onClick: drillDownIds.length > 0 ? () => onDrill(drillDownIds, point.label) : undefined,
    };
  });
};
